import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { assignTutorBadge, viewTutorDetail } from '../../slice/tutorSlice'
import moment from 'moment'
import { hasPermission } from '../../common/permissions'
import Swal from 'sweetalert2'

// ── File type detection ─────────────────────────────────────────────────
const getFileType = (url) => {
    if (!url) return 'unknown'
    try {
        const pathname = new URL(url).pathname.toLowerCase()
        if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(pathname)) return 'image'
        if (/\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(pathname)) return 'video'
        if (/\.pdf$/i.test(pathname)) return 'pdf'
    } catch {
        const lower = url.toLowerCase()
        if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)/.test(lower)) return 'image'
        if (/\.(mp4|webm|ogg|mov|avi|mkv)/.test(lower)) return 'video'
        if (/\.pdf/.test(lower)) return 'pdf'
    }
    return 'image' // default fallback
}

const ViewTutorDetail = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()

    const tutorId = location.state?.tutorId
    const { tutorDetail, loading } = useSelector(state => state.tutor)
    console.log(tutorDetail, "Details");

    const [activeTab, setActiveTab] = useState('personal')
    const [previewSrc, setPreviewSrc] = useState(null)

    useEffect(() => {
        if (!tutorId) { navigate('/tutors'); return }
        dispatch(viewTutorDetail({ id: tutorId }))
    }, [tutorId, dispatch, navigate])

    if (loading) {
        return (
            <div className="container-fluid px-4 py-3">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        )
    }

    if (!tutorDetail) {
        return (
            <div className="container-fluid px-4 py-3">
                <div className="alert alert-warning">Tutor not found</div>
            </div>
        )
    }

    // ── Helpers ───────────────────────────────────────────────────────────────
    const getStatusBadge = (status) => {
        const map = {
            pending: { bg: 'bg-warning', text: 'Pending' },
            approved: { bg: 'bg-success', text: 'Approved' },
            rejected: { bg: 'bg-danger', text: 'Rejected' },
        }
        const c = map[status] || { bg: 'bg-secondary', text: status }
        return <span className={`badge ${c.bg}`}>{c.text}</span>
    }

    const getGenderBadge = (gender) =>
        gender === 'male' ? <span className="badge bg-primary">Male</span> :
            gender === 'female' ? <span className="badge bg-info">Female</span> :
                <span className="badge bg-secondary">Not Selected</span>

    const MediaPreviewModal = ({ src, onClose }) => {
        if (!src) return null
        const type = getFileType(src)

        return (
            <div
                className="modal d-block"
                style={{ backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 9999 }}
                onClick={onClose}
            >
                <div className="modal-dialog modal-dialog-centered modal-lg" onClick={e => e.stopPropagation()}>
                    <div className="modal-content bg-transparent border-0">
                        <div className="modal-body p-0 text-center">
                            {type === 'video' && (
                                <video
                                    src={src}
                                    controls
                                    autoPlay
                                    style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: '8px' }}
                                />
                            )}
                            {/* {type === 'pdf' && (
                                <iframe
                                    src={src}
                                    title="PDF Preview"
                                    style={{
                                        width: '100%', height: '80vh', borderRadius: '8px',
                                        border: 'none', background: '#fff'
                                    }}
                                />
                            )} */}
                            {type === 'pdf' && (
                                <iframe
                                    src={`https://docs.google.com/viewer?url=${encodeURIComponent(src)}&embedded=true`}
                                    title="PDF Preview"
                                    style={{
                                        width: '100%',
                                        height: '80vh',
                                        borderRadius: '8px',
                                        border: 'none',
                                        background: '#fff'
                                    }}
                                />
                            )}
                            {type === 'image' && (
                                <img
                                    src={src}
                                    alt="Preview"
                                    style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: '8px' }}
                                />
                            )}
                            <div className="mt-3 d-flex justify-content-center gap-2">
                                <a
                                    href={src}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-outline-light"
                                    onClick={e => e.stopPropagation()}
                                >
                                    <i className="fe-external-link me-1" /> Open in new tab
                                </a>
                                <button className="btn btn-light" onClick={onClose}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    // ── Document media renderer ────────────────────────────────────────────
    const renderDocumentMedia = (src, label) => {
        const type = getFileType(src)

        if (type === 'video') {
            return (
                <div
                    className="border rounded p-2 text-center"
                    style={{ cursor: 'pointer', backgroundColor: '#f8f9fa', position: 'relative' }}
                    onClick={() => setPreviewSrc(src)}
                >
                    <video
                        src={src}
                        muted
                        preload="metadata"
                        style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '6px' }}
                    />
                    <div style={{
                        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                        width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(0,0,0,0.6)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        <i className="fe-play" style={{ color: '#fff', fontSize: '20px', marginLeft: '3px' }} />
                    </div>
                    <p className="small text-muted mt-2 mb-0">Click to play</p>
                </div>
            )
        }

        if (type === 'pdf') {
            return (
                <div
                    className="border rounded p-3 text-center"
                    style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                    onClick={() => setPreviewSrc(src)}
                >
                    <i className="fe-file-text" style={{ fontSize: '48px', color: '#dc3545' }} />
                    <p className="fw-semibold mt-2 mb-1" style={{ fontSize: '13px', color: '#495057' }}>PDF Document</p>
                    <p className="small text-muted mb-0">Click to view</p>
                </div>
            )
        }

        // Default: image
        return (
            <div
                className="border rounded p-2 text-center"
                style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                onClick={() => setPreviewSrc(src)}
            >
                <img src={src} alt={label} style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '6px' }} />
                <p className="small text-muted mt-2 mb-0">Click to enlarge</p>
            </div>
        )
    }

    // ── Subscription tab helpers ──────────────────────────────────────────────
    const sub = tutorDetail?.active_subscription

    const isExpired = (endDate) => endDate && moment(endDate).isBefore(moment())

    const getDaysRemaining = (endDate) => {
        if (!endDate) return null
        const diff = moment(endDate).diff(moment(), 'days')
        return diff
    }

    const formatValidity = (val) => {
        if (!val) return 'N/A'
        const map = { monthly: 'Monthly', yearly: 'Yearly', 'half-yearly': 'Half Yearly', weekly: 'Weekly' }
        return map[val?.toLowerCase()] || val
    }

    const formatCurrency = (amount, currency) => {
        if (amount === null || amount === undefined) return 'N/A'
        return `${currency || 'AED'} ${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
    }

    const tabs = [
        { key: 'personal', label: 'Personal Information' },
        { key: 'professional', label: 'Professional Details' },
        { key: 'documents', label: 'Documents' },
        { key: 'subscription', label: 'Subscription' },
        // { key: 'badges', label: 'Badge Suggestions' },
    ]

    const assignBadge = async (badgeId) => {
        try {
            await dispatch(assignTutorBadge({
                tutor_id: tutorId,
                badge_id: badgeId
            }));
            Swal.fire({
                toast: true, position: 'top-end', icon: 'success', backdrop: false,
                title: 'Badge assigned successfully',
                showConfirmButton: false, timer: 1500,
            })
            await dispatch(viewTutorDetail({ id: tutorId }));
        } catch (err) {
            console.log(err);
            Swal.fire({
                icon: 'error',
                title: 'Failed',
                text: 'Failed to assign badge.',
            })
        }
    };

    const BadgeSuggestionsTab = () => {

        const badgeData = tutorDetail?.badge_suggestions;

        if (!badgeData) {
            return (
                <div className="card border-0 shadow-sm">
                    <div className="card-body text-center py-5">
                        <i className="fe-award" style={{ fontSize: 50, color: "#adb5bd" }} />
                        <h5 className="mt-3">No Badge Suggestions</h5>
                    </div>
                </div>
            )
        }

        const renderBadgeCard = (badge, type) => {

            const progress =
                badgeData.completed_bookings >= badge.min_completed_bookings
                    ? 100
                    : Math.round(
                        (badgeData.completed_bookings /
                            badge.min_completed_bookings) *
                        100
                    );

            return (
                <div className="col-md-6 col-xl-4 mb-4" key={badge.id}>
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body text-center">

                            <img
                                src={badge.image}
                                alt={badge.name}
                                style={{
                                    width: 70,
                                    height: 70,
                                    objectFit: "contain"
                                }}
                            />

                            <h5 className="mt-3 mb-1">{badge.name}</h5>

                            <p className="text-muted mb-2">
                                Unlock at{" "}
                                <strong>{badge.min_completed_bookings}</strong>{" "}
                                completed sessions
                            </p>

                            {type === "assigned" && (
                                <>
                                    <span className="badge bg-success mb-3">
                                        Assigned
                                    </span>

                                    <button
                                        className="btn btn-success w-100"
                                        disabled
                                    >
                                        <i className="fe-check me-2"></i>
                                        Already Assigned
                                    </button>
                                </>
                            )}

                            {type === "eligible" && (
                                <>
                                    <span className="badge bg-warning text-dark mb-3">
                                        Ready to Assign
                                    </span>

                                    <button
                                        className="btn btn-primary w-100"
                                        onClick={() =>
                                            assignBadge(badge.id)
                                        }
                                    >
                                        <i className="fe-award me-2"></i>
                                        Assign Badge
                                    </button>
                                </>
                            )}

                            {type === "upcoming" && (
                                <>
                                    <span className="badge bg-secondary mb-3">
                                        Locked
                                    </span>

                                    <p className="mb-1">
                                        {badge.sessions_remaining} Sessions Remaining
                                    </p>

                                    <div
                                        className="progress mb-3"
                                        style={{ height: 8 }}
                                    >
                                        <div
                                            className="progress-bar"
                                            style={{
                                                width: `${progress}%`
                                            }}
                                        />
                                    </div>

                                    <button
                                        className="btn btn-light w-100"
                                        disabled
                                    >
                                        Locked
                                    </button>
                                </>
                            )}

                        </div>
                    </div>
                </div>
            )
        }

        return (
            <div className="row">

                <div className="col-12 mb-4">

                    <div className="card border-0 shadow-sm">
                        <div className="card-body d-flex justify-content-between align-items-center">

                            <div>
                                <h5 className="mb-1">
                                    Badge Suggestions
                                </h5>

                                <p className="text-muted mb-0">
                                    Completed Sessions
                                </p>
                            </div>

                            <div
                                style={{
                                    width: 70,
                                    height: 70,
                                    borderRadius: "50%",
                                    background: "#0d6efd",
                                    color: "#fff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: 700,
                                    fontSize: 24
                                }}
                            >
                                {badgeData.completed_bookings}
                            </div>

                        </div>
                    </div>

                </div>

                {badgeData.assigned?.length > 0 && (
                    <>
                        <div className="col-12">
                            <h5 className="mb-3 text-success">
                                <i className="fe-check-circle me-2"></i>
                                Assigned Badges
                            </h5>
                        </div>

                        {badgeData.assigned.map(badge =>
                            renderBadgeCard(badge, "assigned")
                        )}
                    </>
                )}

                {badgeData.eligible?.length > 0 && (
                    <>
                        <div className="col-12">
                            <h5 className="mb-3 text-primary">
                                <i className="fe-award me-2"></i>
                                Ready to Assign
                            </h5>
                        </div>

                        {badgeData.eligible.map(badge =>
                            renderBadgeCard(badge, "eligible")
                        )}
                    </>
                )}

                {badgeData.upcoming?.length > 0 && (
                    <>
                        <div className="col-12">
                            <h5 className="mb-3 text-secondary">
                                <i className="fe-lock me-2"></i>
                                Upcoming Badges
                            </h5>
                        </div>

                        {badgeData.upcoming.map(badge =>
                            renderBadgeCard(badge, "upcoming")
                        )}
                    </>
                )}

            </div>
        );
    };

    // ── Subscription tab UI ───────────────────────────────────────────────────
    const SubscriptionTab = () => {
        if (!sub) {
            return (
                <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                    <div className="card-body p-5 text-center">
                        <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                            <i className="fe-credit-card" style={{ fontSize: '32px', color: '#adb5bd' }} />
                        </div>
                        <h6 className="text-muted mb-1">No Active Subscription</h6>
                        <p className="text-muted small mb-0">This tutor has not subscribed to any plan yet.</p>
                    </div>
                </div>
            )
        }

        const isIAP = sub.subscription_type === 'iap'
        const expired = isExpired(sub.end_date)
        const daysLeft = getDaysRemaining(sub.end_date)
        const isExpiringSoon = daysLeft !== null && daysLeft <= 7 && daysLeft >= 0

        return (
            <div className="row g-3">

                {/* ── Status banner ────────────────────────────────────────── */}
                <div className="col-12">
                    <div
                        style={{
                            borderRadius: '12px',
                            padding: '20px 24px',
                            background: expired
                                ? 'linear-gradient(135deg, #dc3545, #c82333)'
                                : isExpiringSoon
                                    ? 'linear-gradient(135deg, #fd7e14, #e96b02)'
                                    : 'linear-gradient(135deg, #28a745, #20c997)',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: '12px',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <i className={`fe-${isIAP ? 'smartphone' : 'user-check'}`} style={{ fontSize: '22px' }} />
                            </div>
                            <div>
                                <div style={{ fontSize: '18px', fontWeight: '700' }}>
                                    {sub.subscription_plan?.name || sub.product_name || 'Manual Plan'}
                                </div>
                                <div style={{ fontSize: '13px', opacity: 0.85, marginTop: '2px' }}>
                                    {isIAP ? 'In-App Purchase' : 'Manually Assigned by Admin'}
                                </div>
                            </div>
                        </div>

                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '13px', opacity: 0.85 }}>
                                {expired ? 'Expired' : isExpiringSoon ? 'Expiring Soon' : 'Status'}
                            </div>
                            <div style={{ fontSize: '15px', fontWeight: '700', marginTop: '2px' }}>
                                {expired
                                    ? `Expired ${Math.abs(daysLeft)} day${Math.abs(daysLeft) !== 1 ? 's' : ''} ago`
                                    : daysLeft === 0
                                        ? 'Expires today'
                                        : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining`
                                }
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Plan details card ─────────────────────────────────────── */}
                <div className="col-md-6">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
                        <div className="card-body p-4">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="fe-package" style={{ fontSize: '16px', color: '#28a745' }} />
                                </div>
                                <h6 className="mb-0 fw-semibold">Plan Details</h6>
                            </div>

                            <div className="row g-3">
                                <div className="col-6">
                                    <p className="text-muted small mb-1">Plan Name</p>
                                    <p className="mb-0 fw-semibold" style={{ fontSize: '14px' }}>
                                        {sub.subscription_plan?.name || sub.product_name || 'N/A'}
                                    </p>
                                </div>

                                <div className="col-6">
                                    <p className="text-muted small mb-1">Plan Type</p>
                                    <span style={{
                                        fontSize: '12px', fontWeight: '600', padding: '3px 10px', borderRadius: '20px',
                                        background: isIAP ? '#fef3c7' : '#e0f2fe',
                                        color: isIAP ? '#92400e' : '#0369a1',
                                    }}>
                                        {isIAP ? 'IAP' : 'Manual'}
                                    </span>
                                </div>

                                <div className="col-6">
                                    <p className="text-muted small mb-1">Validity</p>
                                    <p className="mb-0 fw-semibold" style={{ fontSize: '14px' }}>
                                        {formatValidity(sub.plan_validity)}
                                    </p>
                                </div>

                                <div className="col-6">
                                    <p className="text-muted small mb-1">Amount Paid</p>
                                    <p className="mb-0 fw-semibold" style={{ fontSize: '14px' }}>
                                        {formatCurrency(sub.amount_paid, sub.currency)}
                                    </p>
                                </div>

                                {isIAP && (
                                    <>
                                        <div className="col-6">
                                            <p className="text-muted small mb-1">Device</p>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <i className={`fe-${sub.device_type === 'ios' ? 'twitter' : sub.device_type === 'android' ? 'smartphone' : 'monitor'}`} style={{ fontSize: '14px', color: '#6c757d' }} />
                                                <p className="mb-0 fw-semibold" style={{ fontSize: '14px', textTransform: 'capitalize' }}>
                                                    {sub.device_type || 'N/A'}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="col-6">
                                            <p className="text-muted small mb-1">Auto Renewing</p>
                                            <span style={{
                                                fontSize: '12px', fontWeight: '600', padding: '3px 10px', borderRadius: '20px',
                                                background: sub.auto_renewing ? '#d1fae5' : '#fee2e2',
                                                color: sub.auto_renewing ? '#065f46' : '#991b1b',
                                            }}>
                                                {sub.auto_renewing ? 'Yes' : 'No'}
                                            </span>
                                        </div>

                                        <div className="col-12">
                                            <p className="text-muted small mb-1">Product ID</p>
                                            <p className="mb-0 fw-semibold" style={{ fontSize: '13px', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                                {sub.product_id || 'N/A'}
                                            </p>
                                        </div>
                                    </>
                                )}

                                {!isIAP && sub.admin && (
                                    <div className="col-12">
                                        <p className="text-muted small mb-1">Assigned By</p>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#e0f2fe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <i className="fe-user" style={{ fontSize: '13px', color: '#0369a1' }} />
                                            </div>
                                            <div>
                                                <p className="mb-0 fw-semibold" style={{ fontSize: '13px' }}>Admin #{sub.admin.id}</p>
                                                <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>{sub.admin.email}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Timeline card ─────────────────────────────────────────── */}
                <div className="col-md-6">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
                        <div className="card-body p-4">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#e8f4fd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="fe-calendar" style={{ fontSize: '16px', color: '#0d6efd' }} />
                                </div>
                                <h6 className="mb-0 fw-semibold">Subscription Timeline</h6>
                            </div>

                            {/* Timeline */}
                            <div style={{ position: 'relative', paddingLeft: '24px' }}>

                                {/* Vertical line */}
                                <div style={{ position: 'absolute', left: '7px', top: '8px', bottom: '8px', width: '2px', background: '#dee2e6', borderRadius: '2px' }} />

                                {/* Subscribed date */}
                                <div style={{ position: 'relative', marginBottom: '20px' }}>
                                    <div style={{ position: 'absolute', left: '-21px', top: '3px', width: '10px', height: '10px', borderRadius: '50%', background: '#0d6efd', border: '2px solid #fff', boxShadow: '0 0 0 2px #0d6efd' }} />
                                    <p className="text-muted small mb-1">Subscribed On</p>
                                    <p className="mb-0 fw-semibold" style={{ fontSize: '14px' }}>
                                        {sub.subscription_date
                                            ? moment(sub.subscription_date).format('DD MMM YYYY, hh:mm A')
                                            : 'N/A'}
                                    </p>
                                </div>

                                {/* Start date */}
                                <div style={{ position: 'relative', marginBottom: '20px' }}>
                                    <div style={{ position: 'absolute', left: '-21px', top: '3px', width: '10px', height: '10px', borderRadius: '50%', background: '#28a745', border: '2px solid #fff', boxShadow: '0 0 0 2px #28a745' }} />
                                    <p className="text-muted small mb-1">Start Date</p>
                                    <p className="mb-0 fw-semibold" style={{ fontSize: '14px' }}>
                                        {sub.start_date
                                            ? moment(sub.start_date).format('DD MMM YYYY, hh:mm A')
                                            : 'N/A'}
                                    </p>
                                </div>

                                {/* End date */}
                                <div style={{ position: 'relative' }}>
                                    <div style={{ position: 'absolute', left: '-21px', top: '3px', width: '10px', height: '10px', borderRadius: '50%', background: expired ? '#dc3545' : isExpiringSoon ? '#fd7e14' : '#28a745', border: '2px solid #fff', boxShadow: `0 0 0 2px ${expired ? '#dc3545' : isExpiringSoon ? '#fd7e14' : '#28a745'}` }} />
                                    <p className="text-muted small mb-1">End Date</p>
                                    <p className="mb-0 fw-semibold" style={{ fontSize: '14px', color: expired ? '#dc3545' : isExpiringSoon ? '#fd7e14' : 'inherit' }}>
                                        {sub.end_date
                                            ? moment(sub.end_date).format('DD MMM YYYY, hh:mm A')
                                            : 'N/A'}
                                    </p>
                                    {isExpiringSoon && !expired && (
                                        <span style={{ fontSize: '11px', color: '#fd7e14', fontWeight: '600' }}>
                                            <i className="fe-alert-circle me-1" />
                                            Expiring in {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                                        </span>
                                    )}
                                    {expired && (
                                        <span style={{ fontSize: '11px', color: '#dc3545', fontWeight: '600' }}>
                                            <i className="fe-x-circle me-1" />
                                            Expired
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Progress bar */}
                            {sub.start_date && sub.end_date && (() => {
                                const total = moment(sub.end_date).diff(moment(sub.start_date), 'seconds')
                                const elapsed = moment().diff(moment(sub.start_date), 'seconds')
                                const pct = Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)))
                                const barColor = expired ? '#dc3545' : isExpiringSoon ? '#fd7e14' : '#28a745'
                                return (
                                    <div style={{ marginTop: '20px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                            <span className="text-muted small">Plan usage</span>
                                            <span className="small fw-semibold" style={{ color: barColor }}>{pct}%</span>
                                        </div>
                                        <div style={{ height: '6px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                            <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: '10px', transition: 'width 0.4s ease' }} />
                                        </div>
                                    </div>
                                )
                            })()}
                        </div>
                    </div>
                </div>

                {/* ── Subscription ID row ───────────────────────────────────── */}
                <div className="col-12">
                    <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                        <div className="card-body p-3">
                            <div className="row g-2 align-items-center">
                                <div className="col-md-3 col-6">
                                    <p className="text-muted small mb-1">Subscription ID</p>
                                    <p className="mb-0 fw-semibold" style={{ fontSize: '13px', fontFamily: 'monospace' }}>#{sub.id}</p>
                                </div>
                                <div className="col-md-3 col-6">
                                    <p className="text-muted small mb-1">Plan ID</p>
                                    <p className="mb-0 fw-semibold" style={{ fontSize: '13px', fontFamily: 'monospace' }}>
                                        {sub.subscription_plan_id ? `#${sub.subscription_plan_id}` : 'N/A'}
                                    </p>
                                </div>
                                <div className="col-md-3 col-6">
                                    <p className="text-muted small mb-1">Record Created</p>
                                    <p className="mb-0 fw-semibold" style={{ fontSize: '13px' }}>
                                        {sub.createdAt ? moment(sub.createdAt).format('DD MMM YYYY, hh:mm A') : 'N/A'}
                                    </p>
                                </div>
                                <div className="col-md-3 col-6">
                                    <p className="text-muted small mb-1">Overall Status</p>
                                    <span style={{
                                        fontSize: '12px', fontWeight: '600', padding: '3px 10px', borderRadius: '20px',
                                        background: expired ? '#fee2e2' : '#d1fae5',
                                        color: expired ? '#991b1b' : '#065f46',
                                    }}>
                                        {expired ? 'Expired' : 'Active'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        )
    }

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="container-fluid px-4 py-3">

            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center gap-3">
                    <button onClick={() => navigate('/tutors')} className="btn btn-outline-secondary" style={{ width: '36px', height: '36px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px' }}>
                        <i className="fe-arrow-left" style={{ fontSize: '16px' }} />
                    </button>
                    <h4 className="mb-0 fw-semibold">Tutor Details</h4>
                </div>
                {hasPermission('tutors', 'edit') && (
                    <Link to="/tutors/edit" state={{ tutorId }} className="btn btn-primary" style={{ padding: '8px 20px', fontSize: '14px', fontWeight: '500', borderRadius: '6px' }}>
                        <i className="fe-edit me-2" style={{ fontSize: '14px' }} />
                        Edit Tutor
                    </Link>
                )}
            </div>

            {/* Profile card */}
            <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '8px' }}>
                <div className="card-body p-4">
                    <div className="row align-items-center">
                        <div className="col-auto">
                            <img
                                src={tutorDetail.profile_picture || process.env.PUBLIC_URL + '/assets/avatar.png'}
                                alt="Profile"
                                onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = process.env.PUBLIC_URL + '/assets/avatar.png' }}
                                style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #dee2e6' }}
                            />
                        </div>
                        <div className="col">
                            <h3 className="mb-2 fw-bold">
                                {`${tutorDetail.first_name || ''} ${tutorDetail.last_name || ''}`.trim() || 'N/A'}
                            </h3>
                            <p className="text-muted mb-2"><i className="fe-mail me-2" />{tutorDetail.email || 'N/A'}</p>
                            <p className="text-muted mb-2">
                                <i className="fe-phone me-2" />
                                {tutorDetail.country_code && tutorDetail.phone_number
                                    ? `${tutorDetail.country_code}${tutorDetail.phone_number}` : 'N/A'}
                            </p>
                            <div className="d-flex gap-2 flex-wrap">
                                {getStatusBadge(tutorDetail.profile_status)}
                                {getGenderBadge(tutorDetail.gender)}
                                <span className={`badge ${tutorDetail.is_active === 1 ? 'bg-success' : 'bg-danger'}`}>
                                    {tutorDetail.is_active === 1 ? 'Active' : 'Inactive'}
                                </span>
                                {tutorDetail.is_premium_verified === 1 && (
                                    <span className="badge bg-warning text-dark"><i className="fe-star me-1" />Premium</span>
                                )}
                                {tutorDetail.is_mohre_verification === 1 && (
                                    <span className="badge bg-primary"><i className="fe-check me-1" />MoHRE Permit Holder</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '8px' }}>
                <div className="card-body p-0">
                    <ul className="nav nav-tabs" style={{ borderBottom: '1px solid #dee2e6' }}>
                        {tabs.map(tab => (
                            <li key={tab.key} className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab.key)}
                                    style={{
                                        border: 'none',
                                        borderBottom: activeTab === tab.key ? '2px solid #0d6efd' : '2px solid transparent',
                                        color: activeTab === tab.key ? '#0d6efd' : '#6c757d',
                                        fontWeight: '500',
                                        padding: '12px 24px',
                                    }}
                                >
                                    {tab.key === 'subscription' && tutorDetail?.active_subscription && (
                                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: isExpired(tutorDetail.active_subscription?.end_date) ? '#dc3545' : '#28a745', display: 'inline-block', marginRight: '6px', marginBottom: '1px' }} />
                                    )}
                                    {tab.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Tab content */}
            {activeTab === 'personal' && (
                <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                    <div className="card-body p-4">
                        <h5 className="mb-4 fw-semibold">Personal Information</h5>
                        <div className="row g-4">
                            <div className="col-md-6"><label className="text-muted small mb-1">User ID</label><p className="mb-0 fw-semibold">{tutorDetail.id || 'N/A'}</p></div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Username</label><p className="mb-0 fw-semibold">{tutorDetail.username || 'N/A'}</p></div>
                            <div className="col-md-6"><label className="text-muted small mb-1">First Name</label><p className="mb-0 fw-semibold">{tutorDetail.first_name || 'N/A'}</p></div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Last Name</label><p className="mb-0 fw-semibold">{tutorDetail.last_name || 'N/A'}</p></div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Email</label><p className="mb-0 fw-semibold">{tutorDetail.email || 'N/A'}</p></div>
                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Phone Number</label>
                                <p className="mb-0 fw-semibold">{tutorDetail.country_code && tutorDetail.phone_number ? `${tutorDetail.country_code}${tutorDetail.phone_number}` : 'N/A'}</p>
                            </div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Date of Birth</label><p className="mb-0 fw-semibold">{tutorDetail.dob ? moment(tutorDetail.dob).format('DD MMM YYYY') : 'N/A'}</p></div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Gender</label><p className="mb-0">{getGenderBadge(tutorDetail.gender)}</p></div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Emirates ID</label><p className="mb-0 fw-semibold">{tutorDetail.emirates_id || 'N/A'}</p></div>
                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Emirate/State</label>
                                <p className="mb-0 fw-semibold">
                                    {tutorDetail.emirate?.length > 0
                                        ? tutorDetail.emirate.map((emt, i) => (
                                            <span key={emt.id ?? i}>
                                                {emt.en_name}
                                                {i < tutorDetail.emirate.length - 1 ? ', ' : ''}
                                            </span>
                                        ))
                                        : <span className="text-muted">N/A</span>}
                                </p>
                            </div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Language Preference</label><p className="mb-0 fw-semibold">{tutorDetail.lang === 'en' ? 'English' : tutorDetail.lang === 'ar' ? 'Arabic' : 'N/A'}</p></div>
                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Languages Spoken</label>
                                <div className="d-flex flex-wrap gap-2">
                                    {tutorDetail.languages_spoken?.length > 0
                                        ? tutorDetail.languages_spoken.map((lang, i) => <span key={i} className="badge bg-info">{lang}</span>)
                                        : <span className="text-muted">N/A</span>}
                                </div>
                            </div>
                            <div className="col-md-12"><label className="text-muted small mb-1">Bio</label><p className="mb-0 fw-semibold" style={{ whiteSpace: 'pre-wrap' }}>{tutorDetail.bio || 'N/A'}</p></div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Created Date</label><p className="mb-0 fw-semibold">{tutorDetail.createdAt ? moment(tutorDetail.createdAt).local().format('DD MMM YYYY, hh:mm A') : 'N/A'}</p></div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Last Updated</label><p className="mb-0 fw-semibold">{tutorDetail.updatedAt ? moment(tutorDetail.updatedAt).local().format('DD MMM YYYY, hh:mm A') : 'N/A'}</p></div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'professional' && (
                <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                    <div className="card-body p-4">
                        <h5 className="mb-4 fw-semibold">Professional Details</h5>
                        <div className="row g-4">
                            <div className="col-md-6"><label className="text-muted small mb-1">Educational Qualification</label><p className="mb-0 fw-semibold">{tutorDetail.educational_qualification || 'N/A'}</p></div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Subjects Taught</label><p className="mb-0 fw-semibold">{tutorDetail.subjects_taught || 'N/A'}</p></div>
                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Subjects</label>
                                <div className="d-flex flex-wrap gap-2">
                                    {tutorDetail.subjects?.length > 0 ? tutorDetail.subjects.map(s => <span key={s.id} className="badge bg-primary" style={{ fontSize: '13px', padding: '6px 12px' }}>{s.option_en}</span>) : <span className="text-muted">N/A</span>}
                                </div>
                            </div>

                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Lesson Format</label>
                                <div className="d-flex flex-wrap gap-2">
                                    {
                                        tutorDetail?.lesson_format ?
                                            <span className="badge bg-secondary" style={{ fontSize: '13px', padding: '6px 12px' }}>{tutorDetail.lesson_format?.option_en}</span> : <span className="text-muted">N/A</span>
                                    }

                                </div>
                            </div>

                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Curriculum</label>
                                <div className="d-flex flex-wrap gap-2">
                                    {tutorDetail.curriculums?.length > 0 ? tutorDetail.curriculums.map(c => <span key={c.id} className="badge bg-success" style={{ fontSize: '13px', padding: '6px 12px' }}>{c.option_en}</span>) : <span className="text-muted">N/A</span>}
                                </div>
                            </div>

                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Area Covered</label>
                                <div className="d-flex flex-wrap gap-2">
                                    {tutorDetail.area_covered || 'N/A'}
                                </div>
                            </div>

                            <div className="col-md-12">
                                <label className="text-muted small mb-1">Grades</label>
                                <div className="d-flex flex-wrap gap-2">
                                    {tutorDetail.grade_details?.length > 0 ? tutorDetail.grade_details.map(g => <span key={g.id} className="badge bg-warning text-dark" style={{ fontSize: '13px', padding: '6px 12px' }}>{g.option_en}</span>) : <span className="text-muted">N/A</span>}
                                </div>
                            </div>
                            <div className="col-md-12">
                                <label className="text-muted small mb-1">Age Range</label>
                                <div className="d-flex flex-wrap gap-2">
                                    {tutorDetail.age_range_details?.length > 0 ? tutorDetail.age_range_details.map(a => <span key={a.id} className="badge bg-info" style={{ fontSize: '13px', padding: '6px 12px' }}>{a.name}</span>) : <span className="text-muted">N/A</span>}
                                </div>
                            </div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Commission Percentage</label><p className="mb-0 fw-semibold">{tutorDetail.commission_percentage || 0}%</p></div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Profile Step</label><p className="mb-0 fw-semibold">{tutorDetail.step || 0}</p></div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'documents' && (
                <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                    <div className="card-body p-4">
                        <h5 className="mb-4 fw-semibold">Documents</h5>
                        <div className="row g-4">
                            {[
                                { label: 'Profile Picture', key: 'profile_picture' },
                                { label: 'Emirates ID - Front', key: 'emirates_id_front' },
                                { label: 'Emirates ID - Back', key: 'emirates_id_back' },
                                { label: 'Tutor Permit - Front', key: 'tutor_permit_front' },
                                { label: 'Tutor Permit - Back', key: 'tutor_permit_back' },
                                { label: 'Educational Certificate', key: 'education_certificate' },
                            ].map(({ label, key }) => (
                                <div key={key} className="col-md-6">
                                    <label className="text-muted small mb-2 d-block">{label}</label>
                                    {tutorDetail[key] ? (
                                        renderDocumentMedia(tutorDetail[key], label)
                                    ) : (
                                        <p className="text-muted">Not uploaded</p>
                                    )}
                                </div>
                            ))}
                            <div className="col-md-6">
                                <label className="text-muted small mb-2 d-block">Video Introduction</label>
                                {tutorDetail.video_introduction ? (
                                    renderDocumentMedia(tutorDetail.video_introduction, 'Video Introduction')
                                ) : (
                                    <p className="text-muted">Not uploaded</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'subscription' && <SubscriptionTab />}

            {activeTab === "badges" && <BadgeSuggestionsTab />}

            {previewSrc && <MediaPreviewModal src={previewSrc} onClose={() => setPreviewSrc(null)} />}
        </div>
    )
}

export default ViewTutorDetail